// client/src/pages/Auth/MercadoPagoCallback.jsx (VERSIÓN MEJORADA)
import React, { useEffect, useState, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useLangNavigate } from '../../hooks/useLangNavigate';
import { useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import api from '../../services/api';
import Spinner from '../../components/layout/Spinner';

const MercadoPagoCallback = () => {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const navigate = useLangNavigate();
  const queryClient = useQueryClient();
  const [error, setError] = useState(null);
  const [statusMessage, setStatusMessage] = useState('');

  const effectRan = useRef(false);

  useEffect(() => {
    setStatusMessage(t('mercadoPagoCallback.status.finalizing'));

    // Previene la doble ejecución en modo de desarrollo de React 18
    if (effectRan.current === true && import.meta.env.DEV) {
      return;
    }

    const finalizeConnection = async () => {
      const code = searchParams.get('code');
      if (!code) {
        setError(t('mercadoPagoCallback.error.noAuthCode'));
        return;
      }

      try {
        setStatusMessage(t('mercadoPagoCallback.status.saving'));

        // Esta es la llamada al backend que guarda las credenciales
        await api.post('/payments/mp-oauth-finalize', {
          code,
          code_verifier: localStorage.getItem('mp_code_verifier'),
        });

        setStatusMessage(t('mercadoPagoCallback.status.updating'));

        // ESTRATEGIA SIMPLE Y EFECTIVA

        // 1. Remover completamente la query del cache
        queryClient.removeQueries({
          queryKey: ['mercadoPagoConnectionStatus'],
        });

        // 2. Invalidar para que se refetch automáticamente
        await queryClient.invalidateQueries({
          queryKey: ['mercadoPagoConnectionStatus'],
        });

        // 3. Pequeña pausa para que el backend procese
        await new Promise((resolve) => setTimeout(resolve, 1000));

        // Limpiamos el verifier una vez usado
        localStorage.removeItem('mp_code_verifier');
        setStatusMessage(t('mercadoPagoCallback.status.success'));

        // Redirigimos con un delay mayor para asegurar que el estado se actualice
        setTimeout(() => {
          navigate('/dashboard/settings', { replace: true });
        }, 2000);
      } catch (err) {
        const errorMessage =
          err.response?.data?.message ||
          t('mercadoPagoCallback.error.finalConnectionFailed');
        setError(errorMessage);
        localStorage.removeItem('mp_code_verifier');
      }
    };

    finalizeConnection();

    return () => {
      effectRan.current = true;
    };
  }, [searchParams, navigate, queryClient, t]);

  return (
    <div className="min-h-screen flex flex-col items-center justify-center text-center p-4 bg-gray-50 dark:bg-neutral-900">
      {error ? (
        <div className="bg-white dark:bg-neutral-800 p-8 rounded-xl shadow-lg max-w-lg">
          <h1 className="text-2xl font-bold text-red-500 mb-4">
            {t('mercadoPagoCallback.error.title')}
          </h1>
          <p className="text-gray-600 dark:text-gray-300">{error}</p>
          <button
            onClick={() => navigate('/dashboard/settings')}
            className="mt-6 bg-orange-500 hover:bg-orange-600 text-white font-bold rounded-full px-6 py-2 shadow transition-colors"
          >
            {t('mercadoPagoCallback.error.backButton')}
          </button>
        </div>
      ) : (
        <Spinner message={statusMessage} />
      )}
    </div>
  );
};

export default MercadoPagoCallback;
